import {
  Stack,
  Text,
  Box,
  Heading,
  useDialogControllers,
  TextLink,
  List,
  RadioItem,
  MultiStepModal,
  IconInfoInverted,
  ButtonIcon,
  Dialog,
  Table,
  Checkbox,
  TextField,
  useResponsiveValue,
  TextLinkButton
} from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { useTranslation } from 'react-i18next'

import * as Yup from 'yup'
import { Form, Formik } from 'formik'
import { useParams, useHistory } from 'react-router'
import { isNil } from 'ramda'

import useMediaQuery from '@/hooks/use-media-query'

import { FieldRadioGroup } from '@/components/ds-form'
import { useUpdateStandAloneTrustScore } from '@/hooks/use-standalone-trust-score'
import rentToIncomeVideo from './assets/rent_to_income.mp4'
import trustwortinessVideo from './assets/trustworthiness.mp4'
import rentToIncomePlaceholder from './assets/rent_to_income_placeholder.jpg'
import trustworthinessPlaceholder from './assets/trustworthiness_placeholder.jpg'

import FormFooter from './form-footer'
import { FormBox } from './form-sections'

import FormWrapper from './form-wrapper'

const TenantScreeningForm = ({
  data,
  isLoading,
  refetch,
  hasAvailableCreditChecks
}: {
  data: any
  isLoading: boolean
  refetch: () => void
  hasAvailableCreditChecks: boolean
}) => {
  const { t } = useTranslation()
  const { trustScoreId = '' } = useParams<{ trustScoreId: string }>()
  const history = useHistory()

  const { visible, showModal, closeModal } = useDialogControllers({
    previewModal: false,
    rentToIncomeModal: false
  })

  const { mutateAsync: updateStandAloneTrustScore, isPending: isSavingTrustScore } =
    useUpdateStandAloneTrustScore(trustScoreId)

  const trustworthiness_fields = [
    'id_provided',
    'linkedin_provided',
    'employment_provided',
    'income_provided'
  ]
  const rent_to_income_fields = ['listing_price', 'household_income']
  const tenantScreening_fields = trustworthiness_fields.concat(rent_to_income_fields)

  const tenantScreeningValidationSchema = Yup.object().shape({
    id_provided: Yup.string().oneOf(['1', '0'], 'Required').required(),
    linkedin_provided: Yup.string().oneOf(['1', '0', 'does_not_matter'], 'Required').required(),
    employment_provided: Yup.string().oneOf(['1', '0'], 'Required').required(),
    income_provided: Yup.string().oneOf(['1', '0'], 'Required').required(),
    household_income: Yup.string().when('prepay12Months', {
      is: false,
      then: Yup.string()
        .max(
          13,
          t(
            'l.standalone_trustscore.form.household_income_validation_message21',
            'Must be less than 10 digits'
          )
        )
        .required(),
      otherwise: Yup.string()
    }),
    listing_price: Yup.string().required(),
    prepay12Months: Yup.boolean()
  })

  const handleSubmit = async (values: any) => {
    const tenantScreeningInfo = {
      tenant: {
        basic_info: {
          first_name: data.tenant.basic_info.first_name,
          last_name: data.tenant.basic_info.last_name,
          dob: data.tenant.basic_info.dob
        },
        identity: data.tenant.identity
      },
      trustworthiness: {
        id_provided: values.id_provided === '1',
        linkedin_provided:
          values.linkedin_provided === 'does_not_matter'
            ? 'does_not_matter'
            : values.linkedin_provided === '1'
              ? 'yes'
              : 'no',
        employment_provided: values.employment_provided === '1',
        income_provided: values.income_provided === '1'
      },
      rent_to_income: {
        household_income: values?.prepay12Months ? null : values.household_income,
        listing_price: Number(values?.listing_price?.replace(/,/g, ''))
      }
    }

    const tenantScreeningData =
      !data?.tenant?.address?.street_number && !data.tenant?.address?.street_name
        ? tenantScreeningInfo
        : {
            ...tenantScreeningInfo,
            tenant: {
              ...tenantScreeningInfo.tenant,
              address: data.tenant.address
            }
          }

    const result = await updateStandAloneTrustScore(tenantScreeningData)

    return result
  }

  const handleTenantScreeningSubmit = async (values: any, actions: any) => {
    const result = await handleSubmit(values)

    if (result?.response.ok) {
      const destination = hasAvailableCreditChecks ? 'confirmation' : 'payment'
      history.push(`/trust-score/edit/${trustScoreId}/${destination}`)
    }
  }

  const initialValues = {
    id_provided: isNil(data?.trustworthiness?.id_provided)
      ? 'none'
      : data?.trustworthiness?.id_provided
        ? '1'
        : '0',
    linkedin_provided: isNil(data?.trustworthiness?.linkedin_provided)
      ? 'none'
      : data?.trustworthiness?.linkedin_provided === 'does_not_matter'
        ? 'does_not_matter'
        : data?.trustworthiness?.linkedin_provided === 'yes'
          ? '1'
          : '0',
    employment_provided: isNil(data?.trustworthiness?.employment_provided)
      ? 'none'
      : data?.trustworthiness?.employment_provided
        ? '1'
        : '0',
    income_provided: isNil(data?.trustworthiness?.income_provided)
      ? 'none'
      : data?.trustworthiness?.income_provided
        ? '1'
        : '0',
    household_income: isNil(data?.rent_to_income?.household_income)
      ? ''
      : parseInt(data?.rent_to_income?.household_income).toLocaleString(),
    listing_price: data?.rent_to_income?.listing_price
      ? parseInt(data?.rent_to_income?.listing_price).toLocaleString()
      : '',
    prepay12Months:
      !data?.rent_to_income?.listing_price || data?.rent_to_income?.household_income ? false : true
  }

  const showPayment = !hasAvailableCreditChecks

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  return (
    <>
      <MultiStepTenantScreeningModal
        visible={visible.previewModal}
        closeModal={closeModal('previewModal')}
      />
      <RentToIncomeModal
        visible={visible.rentToIncomeModal}
        closeModal={closeModal('rentToIncomeModal')}
      />
      <Formik
        onSubmit={handleTenantScreeningSubmit}
        initialValues={initialValues}
        validationSchema={tenantScreeningValidationSchema}
        enableReinitialize={true}
        validateOnMount={true}
      >
        {({ values, setFieldValue, setTouched, errors, touched }) => {
          const handlePrepay12MonthsChange = (e: any) => {
            setFieldValue('prepay12Months', e.target.checked)
            e.target.checked && setFieldValue('household_income', '')
          }

          const handleNumericInputChange = (e: any, field: string) => {
            if (e.target.value === '') return setFieldValue(field, '')
            const number = parseFloat(e.target.value.replace(/,/g, ''))
            if (isNaN(number)) return values.household_income
            setFieldValue(field, number.toLocaleString())
          }

          const handleOnSave = async () => {
            const touchedFields: { [key: string]: boolean } = {}
            tenantScreening_fields.forEach((field: string) => {
              touchedFields[field] = true
            })

            await setTouched(touchedFields, false)

            const tenantScreeningInfo = {
              tenant: {
                basic_info: {
                  first_name: data.tenant.basic_info.first_name,
                  last_name: data.tenant.basic_info.last_name,
                  dob: data.tenant.basic_info.dob
                },
                identity: data.tenant.identity
              },
              trustworthiness: {
                id_provided: values.id_provided === 'none' ? null : values.id_provided === '1',
                linkedin_provided:
                  values.linkedin_provided === 'none'
                    ? null
                    : values.linkedin_provided === 'does_not_matter'
                      ? 'does_not_matter'
                      : values.linkedin_provided === '1'
                        ? 'yes'
                        : 'no',
                employment_provided:
                  values.employment_provided === 'none' ? null : values.employment_provided === '1',
                income_provided:
                  values.income_provided === 'none' ? null : values.income_provided === '1'
              },
              rent_to_income: {
                household_income: values.prepay12Months ? null : values.household_income,
                listing_price: Number(values?.listing_price?.replace(/,/g, ''))
              }
            }

            const tenantScreeningData =
              !data?.tenant?.address?.street_number && !data.tenant?.address?.street_name
                ? tenantScreeningInfo
                : {
                    ...tenantScreeningInfo,
                    tenant: {
                      ...tenantScreeningInfo.tenant,
                      address: data.tenant.address
                    }
                  }

            const result = await updateStandAloneTrustScore(tenantScreeningData)

            if (result?.response.ok) {
              history.push(`/trust-score`)
            }
          }

          return (
            <FormWrapper>
              <Form>
                <Stack align="left" space="gutter">
                  {!isMobile && (
                    <>
                      <Box marginBottom="small">
                        <Heading level="4">
                          {t('l.credit_usage.sidemenu.tenant_screening.label', 'Tenant Screening')}
                        </Heading>
                      </Box>

                      <Text tone="tertiary">
                        {t(
                          'l.standalone_trustscore.form.tenant_screening_desc',
                          'The following information is used to calculate the tenant’s Trustworthiness and Rent-to-Income Ratio in their Trust Score.'
                        )}{' '}
                        <TextLinkButton onClick={showModal('previewModal')}>
                          {t(
                            'l.standalone_trustscore.form.tenant_screening_view_example',
                            'View example'
                          )}
                        </TextLinkButton>
                      </Text>
                    </>
                  )}

                  <FormBox label={t('l.application.view.trustworthiness.title', 'Trustworthiness')}>
                    <List space="large" type="number">
                      <Stack space="medium">
                        <Text weight="medium">
                          {t(
                            'l.standalone_trustscore.form.trustworthiness_id_provided',
                            'Did the tenant provide a valid ID?'
                          )}{' '}
                          <span style={{ color: vars.foregroundColor.critical }}>*</span>
                        </Text>
                        <FieldRadioGroup
                          label=""
                          required
                          aria-labelledby="id provided"
                          name="id_provided"
                          onChange={(e: any) => setFieldValue('id_provided', e.target.value)}
                        >
                          <RadioItem label={t('common.yes', 'Yes')} value={'1'} />
                          <RadioItem label={t('common.no', 'No')} value={'0'} />
                        </FieldRadioGroup>
                      </Stack>

                      <Stack space="medium">
                        <Text weight="medium">
                          {t(
                            'l.standalone_trustscore.form.trustworthiness_linkedin_provided',
                            'Did the tenant provide a LinkedIn profile?'
                          )}{' '}
                          <span style={{ color: vars.foregroundColor.critical }}>*</span>
                        </Text>
                        <FieldRadioGroup
                          required
                          label=""
                          aria-labelledby="linkedin provided"
                          name="linkedin_provided"
                          onChange={(e: { target: { value: string } }) =>
                            setFieldValue('linkedin_provided', e.target.value)
                          }
                        >
                          <RadioItem label={t('common.yes', 'Yes')} value={'1'} />
                          <RadioItem label={t('common.no', 'No')} value={'0'} />
                          <RadioItem
                            label={t(
                              'l.standalone_trustscore.form.trustworthiness_linkeindin',
                              "This doesn't matter to me"
                            )}
                            value={'does_not_matter'}
                          />
                        </FieldRadioGroup>
                      </Stack>
                      <Stack space="medium">
                        <Text weight="medium">
                          {t(
                            'l.standalone_trustscore.form.trustworthiness_employment_provided',
                            'Did the tenant provide proof of employment?'
                          )}{' '}
                          <span style={{ color: vars.foregroundColor.critical }}>*</span>
                        </Text>
                        <FieldRadioGroup
                          label=""
                          aria-labelledby="employment provided"
                          name="employment_provided"
                          onChange={(e: { target: { value: string } }) =>
                            setFieldValue('employment_provided', e.target.value)
                          }
                        >
                          <RadioItem label={t('common.yes', 'Yes')} value={'1'} />
                          <RadioItem label={t('common.no', 'No')} value={'0'} />
                        </FieldRadioGroup>
                      </Stack>
                      <Stack space="medium">
                        <Text weight="medium">
                          {t(
                            'l.standalone_trustscore.form.trustworthiness_proof_income',
                            'Did the tenant provide proof of income?'
                          )}{' '}
                          <span style={{ color: vars.foregroundColor.critical }}>*</span>
                        </Text>
                        <FieldRadioGroup
                          label=""
                          aria-labelledby="income provided"
                          name="income_provided"
                          onChange={(e: { target: { value: string } }) =>
                            setFieldValue('income_provided', e.target.value)
                          }
                        >
                          <RadioItem label={t('common.yes', 'Yes')} value={'1'} />
                          <RadioItem label={t('common.no', 'No')} value={'0'} />
                        </FieldRadioGroup>
                      </Stack>
                    </List>
                  </FormBox>
                  <FormBox
                    label={t(
                      'l.standalone_trustscore.form.rent_to_income_title',
                      'Rent-to-Income Ratio'
                    )}
                    icon={
                      <ButtonIcon
                        id="rent-to-income-info"
                        label="Rent to income info"
                        variant="transparent"
                        icon={<IconInfoInverted />}
                        onClick={showModal('rentToIncomeModal')}
                        tone="secondary"
                      />
                    }
                  >
                    <List space="large" type="number">
                      <Stack space="medium">
                        <Text weight="medium">
                          {t(
                            'l.standalone_trustscore.form.rent_to_income_listing_price',
                            'What is your listing’s rental price?'
                          )}{' '}
                          <span style={{ color: vars.foregroundColor.critical }}>*</span>
                        </Text>
                        <TextField
                          label={t('l.listing.edit.rental_price.label', 'Rental price')}
                          id="listing_price"
                          prefix="$"
                          value={values.listing_price}
                          onChange={e => handleNumericInputChange(e, 'listing_price')}
                          tone={
                            errors.listing_price && touched.listing_price ? 'critical' : 'neutral'
                          }
                          message={
                            errors.listing_price && touched.listing_price && errors.listing_price
                          }
                        />
                      </Stack>
                      <Stack space="medium">
                        <Text weight="medium">
                          {t(
                            'l.standalone_trustscore.form.household_income',
                            'What is the total household annual income?'
                          )}{' '}
                          <span style={{ color: vars.foregroundColor.critical }}>*</span>
                        </Text>
                        <TextField
                          label={t('l.application.view.annual_income.label', 'Annual Income')}
                          id="household_income"
                          prefix="$"
                          disabled={values?.prepay12Months}
                          value={values.household_income}
                          onChange={e => handleNumericInputChange(e, 'household_income')}
                          tone={
                            errors.household_income && touched.household_income
                              ? 'critical'
                              : 'neutral'
                          }
                          message={
                            errors.household_income && touched.household_income
                              ? errors.household_income
                              : undefined
                          }
                        />
                      </Stack>
                    </List>
                    <Box marginLeft="gutter">
                      <Checkbox
                        id="prepay12Months"
                        disabled={!!values?.household_income}
                        checked={values.prepay12Months}
                        onChange={handlePrepay12MonthsChange}
                        label={t(
                          'l.standalone_trustscore.form.rent_to_income_prepay',
                          'They can prepay 12 months rent'
                        )}
                        message={
                          touched.prepay12Months &&
                          !values.prepay12Months &&
                          !values.household_income
                            ? t(
                                'l.standalone_trustscore.form.household_income_validation_message2',
                                'Required: Please enter an annual income or select the prepay checkbox.'
                              )
                            : undefined
                        }
                        tone={
                          touched.prepay12Months &&
                          !values.prepay12Months &&
                          !values.household_income
                            ? 'critical'
                            : 'neutral'
                        }
                      />
                    </Box>
                  </FormBox>
                  <FormFooter
                    onSave={handleOnSave}
                    saveCta={t('common.save', 'Save')}
                    showPayment={showPayment}
                    isLoading={isLoading}
                    isSubmitting={isSavingTrustScore}
                  />
                </Stack>
              </Form>
            </FormWrapper>
          )
        }}
      </Formik>
    </>
  )
}

export default TenantScreeningForm

function MultiStepTenantScreeningModal({
  visible,
  closeModal
}: {
  visible: boolean
  closeModal: () => void
}) {
  const { t } = useTranslation()
  return (
    <MultiStepModal id="welcomeWizard" visible={visible} handleClose={closeModal} showButtonIcons>
      <MultiStepModal.Step
        headerIllustration={
          <Box
            paddingTop={{ mobile: 'small', tablet: undefined }}
            component="video"
            width="full"
            src={trustwortinessVideo}
            height="full"
            loop
            autoPlay
            playsInline
            muted
            poster={trustworthinessPlaceholder}
          />
        }
        key={`trustworthiness-example`}
        nextText={t('common.next', 'Next')}
      >
        <Stack space="gutter">
          <Box marginY="xxxsmall">
            <Heading level="6" weight="strong">
              {t('l.application.view.trustworthiness.title', 'Trustworthiness')}
            </Heading>
          </Box>
          <Box>
            <Text tone="secondary">
              {t(
                'l.standalone_trustscore.form.trustworthiness_subtitle',
                'Have applicants from outside of liv.rent? Streamline your applications and screen tenants all in one place.'
              )}
            </Text>
          </Box>
        </Stack>
      </MultiStepModal.Step>
      <MultiStepModal.Step
        headerIllustration={
          <Box
            paddingTop={{ mobile: 'small', tablet: undefined }}
            component="video"
            width="full"
            src={rentToIncomeVideo}
            height="full"
            loop
            autoPlay
            playsInline
            muted
            poster={rentToIncomePlaceholder}
          />
        }
        key={`rent-to-income-example`}
        nextText={t('l.profile.edit.got_it.text', 'Got it!')}
        backText={t('common.back', 'Back')}
        nextAction={closeModal}
      >
        <Stack space="gutter">
          <Box marginY="xxxsmall">
            <Heading level="6" weight="strong">
              {t('l.standalone_trustscore.form.rent_to_income_title', 'Rent-to-Income Ratio')}
            </Heading>
          </Box>
          <Box>
            <Text tone="secondary">
              {t(
                'l.standalone_trustscore.form.rent_to_income_subtitle',
                'Assess whether income from potential tenants can sufficiently cover rent payments with enough left over for other expenses and unexpected costs.'
              )}
            </Text>
          </Box>
        </Stack>
      </MultiStepModal.Step>
    </MultiStepModal>
  )
}
function RentToIncomeModal({ visible, closeModal }: { visible: boolean; closeModal: () => void }) {
  const { t } = useTranslation()
  const isMobile = useMediaQuery('(max-width: 997px)')

  const data = [
    {
      ratio: '0-30%',
      rating: t('l.standalone_trustscore.form.rent_to_income_table_low', 'Low'),
      description: t(
        'l.standalone_trustscore.form.rent_to_income_table_row1',
        'This is considered the ideal range as it means tenants are well within their budget and still have ample funds for other expenses.'
      )
    },
    {
      ratio: '31-50%',
      rating: t('l.standalone_trustscore.form.rent_to_income_table_average', 'Average'),
      description: t(
        'l.standalone_trustscore.form.rent_to_income_table_row2',
        'A rent-to-income ratio in this range is considered moderately high as although it is manageable, it can indicate that tenant may have trouble covering unexpected expenses.'
      )
    },
    {
      ratio: '51-100%',
      rating: t('l.standalone_trustscore.form.rent_to_income_table_high', 'High'),
      description: t(
        'l.standalone_trustscore.form.rent_to_income_table_row3',
        `Spending over 50% of one's monthly income on rent is considered financially risky as it leaves little flexibility for other living expenses and unexpected costs.`
      )
    }
  ]

  const columns = [
    {
      Header: t('l.standalone_trustscore.form.rent_to_income_title', 'Rent-to-Income Ratio'),
      accessor: 'ratio',
      gridWidth: { custom: '90px' },
      Cell: (cell: { value: string; row: { original: any } }) => {
        return (
          <Box paddingY="small">
            <Text>{cell?.value}</Text>
          </Box>
        )
      }
    },
    {
      Header: t('l.application.view.trust_score_rating.title', 'Rating'),
      accessor: 'rating',
      gridWidth: { custom: '90px' },
      Cell: (cell: { value: string; row: { original: any } }) => {
        return <Text>{cell?.value}</Text>
      }
    },
    {
      Header: t('l.application.view.description.label', 'Description'),
      accessor: 'description',
      gridWidth: { custom: '410px' },
      Cell: (cell: { value: string; row: { original: any } }) => {
        return <Text>{cell?.value}</Text>
      }
    }
  ]

  const mobileColumns = [
    {
      Header: t('l.standalone_trustscore.form.rent_to_income_title', 'Rent-to-Income Ratio'),
      accessor: 'ratio',
      gridWidth: { custom: '90px' },
      Cell: (cell: { value: string; row: { original: any } }) => {
        return (
          <Box paddingY="small">
            <Text>{cell?.value}</Text>
          </Box>
        )
      }
    },
    {
      Header: t('l.application.view.trust_score_rating.title', 'Rating'),
      accessor: 'rating',
      gridWidth: { custom: '220px' },
      Cell: (cell: { value: string; row: { original: any } }) => {
        return (
          <Box display="flex" flexDirection="column" gap="xsmall">
            <Text weight="strong">{cell?.value}</Text>
            <Text>{cell?.row?.original?.description}</Text>
          </Box>
        )
      }
    }
  ]

  return (
    <Dialog
      width="small"
      id="rent-to-income-Dialog"
      open={visible}
      onClose={closeModal}
      title={t('l.standalone_trustscore.form.rent_to_income_title', 'Rent-to-Income Ratio')}
      description={
        <Box paddingY="small">
          <Text>
            {t(
              'l.standalone_trustscore.form.rent_to_income_dialog_desc',
              'An applicant’s Rent-to-Income Ratio is calculated as a percentage based on their gross monthly income, divided by the amount of their rent payments. The recommended Rent-to-Income in Canada is 30%.'
            )}
          </Text>
        </Box>
      }
    >
      <Stack space="large">
        <Stack space="gutter">
          <Text size="large" weight="medium">
            {t(
              'l.standalone_trustscore.form.rent_to_income_dialog_title',
              'What is a good Rent-to-Income Ratio?'
            )}
          </Text>
          <Box>
            <Table data={data} columns={isMobile ? mobileColumns : columns} width="fit-content" />
          </Box>
          <TextLink
            href="https://www.cmhc-schl.gc.ca/professionals/industry-innovation-and-leadership/industry-expertise/affordable-housing/about-affordable-housing/affordable-housing-in-canada"
            target="_blank"
          >
            {t('common.learn_more', 'Learn more')}
          </TextLink>
        </Stack>
      </Stack>
    </Dialog>
  )
}
